import * as React from 'react';
import { memo, useCallback } from 'react';

import { ArrowLeft, BellOff, Settings } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  INotificationPrefs,
  INotificationPrefsInput,
  INotificationRow,
  IUseNotificationFeed,
} from 'core/hooks';
import { TNotificationsFilter, TNotificationsPane } from 'core/state';
import { dashboardLink } from 'core/utils/dashboardDeepLink';

import * as S from './NotificationCenter.styled';
import { NotificationRow } from './NotificationRow';
import { NotificationSettingsPane } from './NotificationSettingsPane';
import { kindMeta } from './notificationTypes';

interface IProps {
  feed: IUseNotificationFeed;
  pane: TNotificationsPane;
  filter: TNotificationsFilter;
  prefs: INotificationPrefs | null;
  prefsError?: boolean;
  saving: boolean;
  audioUnlocked: boolean;
  soundLocked: boolean;
  alarmActive: boolean;
  onSetPane: (pane: TNotificationsPane) => void;
  onSetFilter: (filter: TNotificationsFilter) => void;
  onClose: () => void;
  onSnooze: () => void;
  onSavePrefs: (input: INotificationPrefsInput) => Promise<boolean>;
  onPreviewSound: () => Promise<boolean>;
  onStartTelegramLink: () => Promise<{
    deepLink: string;
    expiresAt: string;
  } | null>;
  onDropTelegramLink: () => Promise<void>;
  onRefetchPrefs: () => void;
}

/** Содержимое поповера: лента или настройки. */
export const NotificationPanel = memo((props: IProps) => {
  const {
    feed,
    pane,
    filter,
    prefs,
    prefsError,
    saving,
    audioUnlocked,
    soundLocked,
    alarmActive,
    onSetPane,
    onSetFilter,
    onClose,
    onSnooze,
    onSavePrefs,
    onPreviewSound,
    onStartTelegramLink,
    onDropTelegramLink,
    onRefetchPrefs,
  } = props;
  const { t } = useTranslation();
  const history = useHistory();

  const openRow = useCallback(
    (row: INotificationRow) => {
      // Порядок намеренный: сначала переход, потом отметка. Иначе клик ждёт
      // сетевого ответа, и человек успевает решить, что ничего не произошло.
      history.push(kindMeta(row.kind).href(row.taskId));
      if (!row.readAt) feed.markRead([row.id]);
      onClose();
    },
    [feed, history, onClose],
  );

  const openAllTasks = useCallback(() => {
    history.push(dashboardLink({ view: 'tasks', taskMode: 'mine' }));
    onClose();
  }, [history, onClose]);

  const settings = pane === 'settings';

  return (
    <S.Panel>
      <S.PanelHead>
        {settings && (
          <S.IconButton
            type="button"
            aria-label={t('notifications.settings.back')}
            onClick={() => onSetPane('list')}
          >
            <ArrowLeft size={16} />
          </S.IconButton>
        )}
        <S.PanelTitle>
          {settings
            ? t('notifications.settings.title')
            : t('notifications.title')}
        </S.PanelTitle>
        {!settings && feed.unreadCount > 0 && (
          <S.CountPill>{feed.unreadCount}</S.CountPill>
        )}
        <S.HeadSpacer />
        {!settings && (
          <S.TextButton
            type="button"
            disabled={feed.unreadCount === 0}
            onClick={feed.markAllRead}
          >
            {t('notifications.markAllRead')}
          </S.TextButton>
        )}
        {!settings && (
          <S.IconButton
            type="button"
            aria-label={t('notifications.settings.open')}
            onClick={() => onSetPane('settings')}
          >
            <Settings size={16} />
          </S.IconButton>
        )}
      </S.PanelHead>

      {!settings && soundLocked && (
        // Единственный честный способ сказать «звук включён, но браузер
        // молчит»: играть по таймеру мы не пытаемся — это дало бы поток
        // NotAllowedError и ни одного звука.
        <S.Warning>{t('notifications.sound.locked')}</S.Warning>
      )}

      {!settings && alarmActive && (
        <S.Warning>
          {t('notifications.sound.alarmActive')}
          <S.HeadSpacer />
          <S.TextButton type="button" onClick={onSnooze}>
            {t('notifications.sound.snooze')}
          </S.TextButton>
        </S.Warning>
      )}

      {settings ? (
        <NotificationSettingsPane
          prefs={prefs}
          loadError={prefsError}
          saving={saving}
          audioUnlocked={audioUnlocked}
          onSave={onSavePrefs}
          onPreviewSound={onPreviewSound}
          onStartTelegramLink={onStartTelegramLink}
          onDropTelegramLink={onDropTelegramLink}
          onRefetch={onRefetchPrefs}
        />
      ) : (
        <>
          <S.Tabs>
            <S.Tab
              type="button"
              $active={filter === 'all'}
              onClick={() => onSetFilter('all')}
            >
              {t('notifications.tab.all')}
            </S.Tab>
            <S.Tab
              type="button"
              $active={filter === 'unread'}
              onClick={() => onSetFilter('unread')}
            >
              {t('notifications.tab.unread')}
            </S.Tab>
          </S.Tabs>

          <S.List>
            {feed.error && (
              <S.Empty>
                {t('notifications.error')}
                <div>
                  <S.TextButton type="button" onClick={feed.refetch}>
                    {t('notifications.retry')}
                  </S.TextButton>
                </div>
              </S.Empty>
            )}
            {!feed.error && !feed.rows.length && !feed.loading && (
              <S.Empty>
                <BellOff size={20} />
                <div>
                  {filter === 'unread'
                    ? t('notifications.emptyUnread')
                    : t('notifications.empty')}
                </div>
              </S.Empty>
            )}
            {feed.rows.map((row) => (
              <NotificationRow key={row.id} row={row} onOpen={openRow} />
            ))}
            {feed.hasNextPage && (
              <S.Empty>
                <S.TextButton type="button" onClick={feed.loadMore}>
                  {t('notifications.loadMore')}
                </S.TextButton>
              </S.Empty>
            )}
          </S.List>

          <S.PanelFoot>
            <S.TextButton type="button" onClick={openAllTasks}>
              {t('notifications.allTasks')}
            </S.TextButton>
          </S.PanelFoot>
        </>
      )}
    </S.Panel>
  );
});

NotificationPanel.displayName = 'NotificationPanel';
